<template>
    <div ref="raiz">
        <div
            v-for="grupo in gruposComProdutos"
            :key="grupo.descricao"
            class="grupo-section"
        >
            <!-- Título da categoria -->
            <div
                class="category category-heading mt-6 mb-3"
                :style="isDark ? 'color: #ccc;' : 'color: #555555;'"
                :id="grupo.descricao.replace(/\s/g, '-')"
                :data-categoria="grupo.descricao"
            >
                {{ grupo.descricao }}
            </div>

            <!-- Grid de produtos -->
            <div class="products-grid">
                <v-card
                    v-for="(item, index) in grupo.produtos"
                    :key="index"
                    @click="$emit('abrir', item)"
                    class="product-card"
                    elevation="6"
                    tile
                    :style="estiloCard(item)"
                >
                    <!-- Imagem -->
                    <div class="product-img-wrapper">
                        <v-img
                            v-if="imagensProntas"
                            :src="getUrl(item.imagem)"
                            :style="ritmoDoFlutuar(item)"
                            class="product-img-float"
                            height="190"
                            eager
                            cover
                        />
                        <div
                            v-if="configuracoes.exibir_estoque_cardapio && item.controle_estoque && item.estoque > 0"
                            class="stock-chip"
                        >
                            <span class="caption">{{ getEstoqueText(item) }}</span>
                        </div>
                    </div>

                    <!-- Conteúdo textual -->
                    <div class="product-content">
                        <div class="px-3 pt-2 pb-1">
                            <div
                                class="product-title"
                                :style="`color: ${isDark ? '#fff' : '#444'};`"
                            >
                                {{ item.descricao }}
                            </div>
                            <div
                                class="mt-1"
                                :style="`color: ${isDark ? '#bbb' : '#666'}`"
                            >
                                <span class="product-details">{{ item.detalhes }}</span>
                            </div>
                        </div>

                        <!-- Badge fidelidade -->
                        <div
                            v-if="item.pontos_ativo && fidelidade.resgate_ativo"
                            class="px-3 mt-1"
                        >
                            <v-chip
                                color="green lighten-5"
                                text-color="green"
                                small
                                style="font-size: 11px; font-weight: bold;"
                            >
                                {{ $t('produto.resgatePor', { n: Math.round((+item.valor || item.valor_minimo) * item.pontos_multiplicador) }) }}
                            </v-chip>
                        </div>

                        <!-- Preço -->
                        <div v-if="getValor(item)" class="px-3 pb-3 mt-2">
                            <div v-if="+item.valor_antigo" class="mb-1">
                                <v-chip color="green" text-color="white" class="mr-1" label x-small>
                                    {{ getDiscount(item) }}%
                                </v-chip>
                                <span style="text-decoration: line-through; font-size: 13px; color: #757575;">
                                    {{ formatMoney(+item.valor_antigo) }}
                                </span>
                            </div>
                            <div>
                                <span style="color: #4CAF50; font-weight: bold; font-size: 17px;">
                                    {{ getValor(item) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Ícone de fidelidade (canto) -->
                    <div
                        v-if="item.pontos_ativo && fidelidade.resgate_ativo && item.valor"
                        style="position: absolute; top: -5px; right: -5px; display: flex;"
                    >
                        <v-btn fab width="32" height="32" color="primary" readonly>
                            <v-icon small color="white">mdi-gift-outline</v-icon>
                        </v-btn>
                    </div>
                </v-card>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { clone, normalizarBusca } from '@/utils/utils';
import { esgotado } from '@/utils/estoque';

const MARGEM_VISIBILIDADE = '300px';
const ESPERA_FIM_ROLAGEM = 180;

export default {
    name: 'ProdutosGrid',

    props: {
        search: {
            type: String,
            default: '',
        },
        imagensProntas: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        ...mapState([
            'produtos',
            'grupos',
            'configuracoes',
        ]),

        isDark() {
            return this.$vuetify.theme.isDark;
        },

        fidelidade() {
            return this.configuracoes.fidelidade || {};
        },

        gruposComProdutos() {
            const termo = normalizarBusca(this.search);

            return clone(this.grupos)
                .sort((a, b) => (+a.ordem > +b.ordem) ? 1 : ((+b.ordem > +a.ordem) ? -1 : 0))
                .map(grupo => ({
                    descricao: grupo.descricao,
                    produtos: this.produtos.filter(p =>
                        p.grupo.descricao === grupo.descricao
                        && normalizarBusca(p.descricao).includes(termo)
                    ),
                }))
                .filter(grupo => grupo.produtos.length);
        },
    },

    watch: {
        gruposComProdutos() {
            this.$nextTick(this.observarCards);
        },
    },

    created() {
        this.observer = null;
        this.cardsVisiveis = new Set();
        this.motivosDePausa = new Set();
        this.timerRolagem = null;
    },

    mounted() {
        // WebView muito antiga: fica sem o efeito, mas o cardápio funciona igual.
        if (typeof IntersectionObserver === 'undefined') {
            return;
        }

        this.observer = new IntersectionObserver(this.onIntersect, {
            rootMargin: MARGEM_VISIBILIDADE,
        });
        this.observarCards();

        window.addEventListener('scroll', this.onScroll, { passive: true });
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    },

    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        clearTimeout(this.timerRolagem);
    },

    methods: {
        pausarAnimacoes(motivo) {
            this.motivosDePausa.add(motivo);
            this.aplicarPausa();
        },

        retomarAnimacoes(motivo) {
            this.motivosDePausa.delete(motivo);
            this.aplicarPausa();
        },

        aplicarPausa() {
            const pausado = this.motivosDePausa.size > 0;
            this.cardsVisiveis.forEach(el => el.classList.toggle('anim-pausada', pausado));
        },

        onIntersect(entries) {
            const pausado = this.motivosDePausa.size > 0;

            entries.forEach(({ target, isIntersecting }) => {
                if (isIntersecting) {
                    this.cardsVisiveis.add(target);
                    target.classList.add('anim-ativa');
                    target.classList.toggle('anim-pausada', pausado);
                    return;
                }

                this.cardsVisiveis.delete(target);
                target.classList.remove('anim-ativa', 'anim-pausada');
            });
        },

        observarCards() {
            if (!this.observer || !this.$refs.raiz) {
                return;
            }

            this.observer.disconnect();
            this.cardsVisiveis.clear();

            this.$refs.raiz.querySelectorAll('.product-card').forEach((el) => {
                el.classList.remove('anim-ativa', 'anim-pausada');
                this.observer.observe(el);
            });
        },

        onScroll() {
            if (!this.timerRolagem) {
                this.pausarAnimacoes('rolagem');
            } else {
                clearTimeout(this.timerRolagem);
            }

            this.timerRolagem = setTimeout(() => {
                this.timerRolagem = null;
                this.retomarAnimacoes('rolagem');
            }, ESPERA_FIM_ROLAGEM);
        },

        onVisibilityChange() {
            if (document.hidden) {
                this.pausarAnimacoes('oculto');
            } else {
                this.retomarAnimacoes('oculto');
            }
        },

        ritmoDoFlutuar(item) {
            const semente = this.semeadura(item);
            const duracao = 8 + (semente % 5);
            const fase = ((semente >> 3) % 100) / 100;

            return {
                'animation-duration': `${duracao}s`,
                'animation-delay': `${(-fase * duracao).toFixed(2)}s`,
            };
        },

        semeadura(item) {
            return Math.abs(((+item.id || 0) + 1) * 2654435761 % 2147483647);
        },

        estiloCard(item) {
            return {
                'border': `solid 1px ${this.isDark ? '#505050' : '#d8d8d8'}`,
                'border-radius': '14px !important',
                'background-color': `rgb(${this.isDark ? '21 21 21' : '250 250 250'} / 90%) !important`,
                'opacity': this.estoqueDisponivel(item) ? '1' : '0.35',
                'overflow': 'hidden',
            };
        },

        estoqueDisponivel(produto) {
            return !esgotado(produto);
        },

        getValor(produto) {
            return this.estoqueDisponivel(produto)
                ? (+produto.valor_minimo ? this.$t('produto.aPartirDe', { valor: this.formatMoney(produto.valor_minimo) }) : (+produto.valor ? `${this.formatMoney(produto.valor)}` : ''))
                : this.$t('produto.esgotadoCaixaAlta');
        },

        getEstoqueText(item) {
            return item.estoque > 1
                ? this.$t('produto.restamUnidades', { n: item.estoque })
                : this.$t('produto.restaUma');
        },

        getDiscount(item) {
            const preco = item.valor_minimo || item.valor;
            const descontoPercentual = ((item.valor_antigo - preco) / item.valor_antigo) * 100;
            return Math.round(descontoPercentual);
        },

        getUrl(img) {
            if (!img?.url) {
                return require('@/assets/noimageavailable.png');
            }

            return img.tmp
                ? `${this.$urlAPI}/files/tmp/${img.url}`
                : `${this.$urlAPI}/storage/${img.url}`;
        },
    },
};
</script>

<style scoped>
.category {
    font-size: 1.3em;
    font-weight: 700;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.grupo-section {
    margin-bottom: 4px;
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 18px;
    padding: 8px 0 24px;
}

@media (max-width: 1400px) {
    .products-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 960px) {
    .products-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 600px) {
    .products-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
    }
}

.product-card {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    position: relative;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.product-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2) !important;
}

.product-img-wrapper {
    height: 190px;
    overflow: hidden;
    position: relative;
    border-radius: 14px 14px 0 0;
}

.product-img-float {
    transform: scale(1.18);
    width: 100% !important;
    height: 100% !important;
}

.product-card.anim-ativa .product-img-float {
    animation-name: floatCard;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
}

.product-card.anim-pausada .product-img-float {
    animation-play-state: paused;
}

@keyframes floatCard {
    0%   { transform: translateY(0px)   scale(1.18); }
    50%  { transform: translateY(-12px) scale(1.18); }
    100% { transform: translateY(0px)   scale(1.18); }
}

.product-content {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.product-title {
    text-transform: uppercase;
    letter-spacing: 1px;
    -webkit-text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    line-height: 1.2;
    font-weight: bold;
    font-size: 14px;
}

.product-details {
    -webkit-text-size-adjust: 100%;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    font-size: 13px;
    line-height: 18px;
    white-space: pre-line;
}

.stock-chip {
    position: absolute;
    top: 0;
    background: #8009e5;
    color: white;
    border-radius: 5px;
    padding: 0 5px;
}
</style>
